import { escapeHtml as esc } from '../util/escapeHtml.js';
/**
 * Loot-chance popup. Click the "Loot" cell on a Misdeed or Good Deed and this
 * dialog shows the exact weighted drop table — what each item's true % is —
 * plus, for crimes only, the tier-based material drop table.
 *
 * No actions: it's read-only. Esc / click-outside dismisses.
 */
import type { LootTableEntryDto, MaterialPoolTierDto } from '@rex-mortis/shared';

export interface LootChanceOpts {
  /** Heading — e.g. the crime/deed name. */
  title: string;
  /** Overall chance the main loot roll fires on success (0..1). */
  dropChance: number;
  /** Weighted main loot table — gifts on a good deed, swag on a crime. */
  dropTable: LootTableEntryDto[];
  /** Label for the main loot section — defaults to "Loot". */
  dropLabel?: string;
  /** Optional: tier-based material drop alongside the main loot (crimes only). */
  materials?: {
    poolTier: MaterialPoolTierDto;
    dropChance: number;
    dropTable: LootTableEntryDto[];
  };
  /** Optional extra line rendered above the footnote — e.g. a rare independent
   *  drop the weighted tables don't capture (the Morghul mythic on Sparring). */
  note?: string;
  /** Optional second generic loot section rendered after the main (and any
   *  materials) section — e.g. the Crypt's top-damage-only artifact/mythic pool. */
  extraSection?: { label: string; dropChance: number; dropTable: LootTableEntryDto[] };
  /** Optional override for the footnote text (defaults to the crime/deed wording). */
  footnote?: string;
}

export function showLootChanceDialog(opts: LootChanceOpts): void {
  // Idempotent open — replace any existing dialog.
  document.getElementById('loot-modal')?.remove();
  document.getElementById('loot-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'loot-overlay';
  overlay.className = 'loot-overlay';
  document.body.append(overlay);

  const modal = document.createElement('div');
  modal.id = 'loot-modal';
  modal.className = 'loot-modal';
  document.body.append(modal);

  if (!document.getElementById('loot-styles')) {
    const style = document.createElement('style');
    style.id = 'loot-styles';
    style.textContent = LOOT_STYLES;
    document.head.append(style);
  }

  const close = (): void => {
    document.removeEventListener('keydown', onKey);
    overlay.remove();
    modal.remove();
  };
  overlay.addEventListener('click', close);

  modal.innerHTML = `
    <h2 class="loot-title">${esc(opts.title)} — Loot Chances</h2>
    ${renderSection(
      opts.dropLabel ?? 'Loot',
      opts.dropChance,
      opts.dropTable,
    )}
    ${opts.materials
      ? renderSection(
          `Forge Materials (${opts.materials.poolTier})`,
          opts.materials.dropChance,
          opts.materials.dropTable,
        )
      : ''}
    ${opts.extraSection
      ? renderSection(
          opts.extraSection.label,
          opts.extraSection.dropChance,
          opts.extraSection.dropTable,
        )
      : ''}
    ${opts.note ? `<p class="loot-note">${esc(opts.note)}</p>` : ''}
    <div class="loot-actions">
      <button class="btn" id="loot-close">Close</button>
    </div>
    <p class="loot-foot">
      ${esc(
        opts.footnote ??
          'Percentages assume a successful TRIUMPH outcome. The overall drop roll fires first; if it succeeds, one item is picked by its weight share below.',
      )}
    </p>
  `;
  modal.querySelector<HTMLButtonElement>('#loot-close')!.addEventListener('click', close);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  document.addEventListener('keydown', onKey);
}

function renderSection(label: string, overall: number, table: LootTableEntryDto[]): string {
  if (table.length === 0) {
    return `
      <div class="loot-section">
        <div class="loot-section-head">${esc(label)}</div>
        <p class="loot-empty">No drops in this pool.</p>
      </div>
    `;
  }
  const totalWeight = table.reduce((s, e) => s + e.weight, 0) || 1;
  const overallPct = Math.round(overall * 100);
  const rows = table
    .slice()
    .sort((a, b) => b.weight - a.weight)
    .map((e) => {
      const sharePct = (e.weight / totalWeight) * 100;
      const overallItemPct = sharePct * overall;
      const qty = e.qtyMin === e.qtyMax ? `${e.qtyMin}` : `${e.qtyMin}–${e.qtyMax}`;
      return `
        <tr>
          <td>${esc(e.itemName)}</td>
          <td class="num">${qty}</td>
          <td class="num">${sharePct.toFixed(1)}%</td>
          <td class="num gain">${overallItemPct.toFixed(2)}%</td>
        </tr>
      `;
    })
    .join('');
  return `
    <div class="loot-section">
      <div class="loot-section-head">
        ${esc(label)} —
        <span class="loot-overall">${overallPct}% to drop ANY</span>
      </div>
      <table class="loot-table">
        <thead>
          <tr>
            <th>Item</th>
            <th class="num">Qty</th>
            <th class="num">If it drops</th>
            <th class="num">Per attempt</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `;
}

const LOOT_STYLES = `
  .loot-overlay {
    position: fixed;
    inset: 0;
    background: rgba(28, 17, 8, 0.55);
    z-index: 300;
    backdrop-filter: blur(1.5px);
  }
  .loot-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(640px, 96vw);
    max-height: 88vh;
    overflow-y: auto;
    background: var(--c-parchment);
    background-image: var(--parchment-texture);
    border: 1px solid var(--c-rule-strong);
    border-radius: var(--radius-lg);
    box-shadow: 0 0 0 1px var(--c-gold), 0 14px 40px rgba(0, 0, 0, 0.5);
    padding: var(--s-5);
    z-index: 301;
  }
  .loot-title {
    font-family: var(--font-heading);
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-size: 1.2rem;
    color: var(--c-oxblood);
    margin: 0 0 var(--s-3) 0;
    text-align: center;
  }
  .loot-section { margin-bottom: var(--s-4); }
  .loot-section-head {
    font-family: var(--font-heading);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.95rem;
    color: var(--c-ink);
    border-bottom: 1px solid var(--c-rule);
    padding-bottom: 4px;
    margin-bottom: var(--s-2);
  }
  .loot-overall { color: var(--c-gold-text); font-weight: 700; }
  .loot-table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--font-body);
    font-size: 0.95rem;
  }
  .loot-table th, .loot-table td {
    padding: 6px 8px;
    border-bottom: 1px dashed var(--c-rule);
    text-align: left;
    color: var(--c-ink);
  }
  .loot-table th { color: var(--c-ink-soft); font-weight: 600; }
  .loot-table .num { text-align: right; font-family: var(--font-numeric); }
  .loot-table .gain { color: var(--c-success); font-weight: 600; }
  .loot-empty { color: var(--c-ink-soft); font-style: italic; }
  .loot-actions {
    display: flex; justify-content: center; gap: var(--s-3);
    margin-top: var(--s-3);
  }
  .loot-foot {
    margin-top: var(--s-3); margin-bottom: 0;
    color: var(--c-ink-soft); font-size: 0.85rem; line-height: 1.4;
    text-align: center;
  }
  .loot-note {
    margin: var(--s-3) 0 0 0;
    padding: var(--s-2) var(--s-3);
    border: 1px solid var(--c-gold);
    border-radius: var(--radius-md);
    background: rgba(176, 141, 87, 0.10);
    color: var(--c-ink);
    font-size: 0.9rem;
    line-height: 1.45;
    text-align: center;
  }
`;
